import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { disposeConfigFlagsWsClient } from 'application/providers/DataProvider';
import { setAppInitialized } from '@app/store/application';
import { createLogger } from '@shared/lib/logger';

import { getTokenCookie } from '@shared/lib';
import { FLAG_UPDATED_SUBSCRIPTION, FlagEvaluationGql } from '@shared/api/configFlags/configFlagsApi';

import { fetchConfigFlags, setConfigFlag } from '@shared/lib/configFlags';

const logger = createLogger('ConfigFlagsListener', '#c77dff');

type AnyThunkDispatch = ThunkDispatch<any, any, UnknownAction>;

type CreateConfigFlagsListenerType = (
  configFlagsApolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

let subscriptionStarted = false;

export const disconnectConfigFlagsSubscription = (): void => {
  disposeConfigFlagsWsClient();
  subscriptionStarted = false;
};

export const createConfigFlagsListener: CreateConfigFlagsListenerType = (configFlagsApolloClient) => {
  const middleware = createListenerMiddleware();

  middleware.startListening({
    actionCreator: setAppInitialized,
    effect: async (_action, { dispatch: listenerDispatch }) => {
      const dispatch = listenerDispatch as AnyThunkDispatch;
      const token = getTokenCookie();

      if (!token) {
        return;
      }

      dispatch(fetchConfigFlags());

      if (subscriptionStarted) {
        return;
      }

      subscriptionStarted = true;

      configFlagsApolloClient
        .subscribe<{ flagUpdated: FlagEvaluationGql | null }>({ query: FLAG_UPDATED_SUBSCRIPTION })
        .subscribe({
          next: ({ data }) => {
            const flag = data?.flagUpdated;

            if (flag) {
              logger.info('flagUpdated:', flag.flagName, flag.value);
              dispatch(setConfigFlag({ flagName: flag.flagName, value: flag.value }));
            }
          },
          error: (error) => logger.error('subscription error:', error),
        });
    },
  });

  return middleware;
};
